import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Button, Form, Input, Switch, Collapse, Modal } from 'antd';
import { useTranslation } from 'react-i18next';
import { ArrowLeftOutlined, ArrowRightOutlined } from '@ant-design/icons';
import { useState, useRef, useEffect } from 'react';
import { useProfileOnboarding } from '@/context/OnboarrdingContext';

export const Route = createFileRoute('/get-offer-details')({
  component: RouteComponent,
});

function RouteComponent() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const jobDescription = Form.useWatch(['jobDescription'], form);
  const instructionsRef = useRef<any>(null);
  const { profileResponse, contentRequest, generateCVContent } = useProfileOnboarding();
  const [showInstructions, setShowInstructions] = useState(!!contentRequest?.instructions);

  useEffect(() => {
    // If no profile is present, redirect to edit-profile with empty form
    if (!profileResponse?.profile) {
      navigate({
        to: '/import-profile/edit-profile',
        search: { method: 'fill-from-zero' },
        replace: true,
      });
    }
  }, [profileResponse, navigate]);

  const handleSwitchChange = (checked: boolean) => {
    setShowInstructions(checked);
    if (checked) {
      // Wait for the collapse animation to finish before focusing
      setTimeout(() => {
        instructionsRef.current?.resizableTextArea?.textArea?.focus();
      }, 300);
    }
  };

  const onFinish = (values: { jobDescription: string; instructions?: string }) => {
    generateCVContent({
      jobDescription: values.jobDescription,
      instructions: values.instructions,
    });
    navigate({
      to: '/build-documents',
    });
  };

  // If no profile, don't render the form
  if (!profileResponse?.profile) {
    return null;
  }

  return (
    <div className="h-screen w-full flex flex-col gap-8 items-center justify-center overflow-y-auto p-4">
      <header className="text-center pt-12 max-w-[840px]">
        <h1 className="h1 mb-2">{t('get-offer-details.title')}</h1>
        <p className="body-1 text-gray">
          {t('get-offer-details.subtitle')}
          <span className="text-primary">{t('get-offer-details.subtitle_cv')}</span>
          {t('get-offer-details.subtitle_and')}
          <span className="text-primary">{t('get-offer-details.subtitle_cover')}</span>
          {t('get-offer-details.subtitle_end')}
        </p>
      </header>

      <main className="w-full max-w-[640px]">
        <Form
          initialValues={contentRequest || undefined}
          form={form}
          layout="vertical"
          onFinish={onFinish}
          className="flex flex-col gap-4"
        >
          <Form.Item
            name="jobDescription"
            label={t('get-offer-details.job_description')}
            rules={[{ required: true, message: t('global.require_field') }]}
          >
            <Input.TextArea
              rows={8}
              placeholder={t('get-offer-details.job_description_placeholder')}
              className="resize-none"
            />
          </Form.Item>

          <div>
            <div
              className="flex items-center gap-2 mb-2 cursor-pointer"
              onClick={() => handleSwitchChange(!showInstructions)}
            >
              <span className="text-sm">{t('get-offer-details.instructions')}</span>
              <Switch checked={showInstructions} onChange={handleSwitchChange} size="small" />
            </div>

            <div className="instructions-collapse">
              <Collapse
                bordered={false}
                activeKey={showInstructions ? ['1'] : []}
                items={[
                  {
                    key: '1',
                    children: (
                      <Form.Item name="instructions">
                        <Input.TextArea
                          ref={instructionsRef}
                          rows={4}
                          placeholder={t('get-offer-details.instructions_placeholder')}
                          className="resize-none"
                        />
                      </Form.Item>
                    ),
                  },
                ]}
              />
            </div>
          </div>

          <div className="w-full max-w-[450px] mx-auto mt-6 flex flex-col gap-4">
            <Button
              type="primary"
              htmlType="submit"
              icon={<ArrowRightOutlined />}
              iconPosition="end"
              size="large"
              className="w-full"
            >
              {t('get-offer-details.generate')}
            </Button>
            {!jobDescription && (
              <Button
                onClick={() =>
                  Modal.confirm({
                    title: t('get-offer-details.ignore_steps'),
                    content: t('get-offer-details.ignore_steps_description'),
                    okText: t('global.continue'),
                    cancelText: t('global.cancel'),
                    onOk: () => {
                      onFinish({ jobDescription: t('get-offer-details.general_description') });
                    },
                  })
                }
                color="primary"
                variant="outlined"
                icon={<ArrowRightOutlined />}
                iconPosition="start"
                size="large"
                className="w-full"
              >
                {t('get-offer-details.ignore_steps')}
              </Button>
            )}

            <Button
              type="dashed"
              icon={<ArrowLeftOutlined />}
              iconPosition="start"
              size="large"
              className="w-full"
              onClick={() =>
                navigate({
                  to: '/import-profile/edit-profile',
                  search: { method: undefined },
                  replace: true,
                })
              }
            >
              {t('global.come-back')}
            </Button>
          </div>
        </Form>
      </main>
    </div>
  );
}
